/**
 * The placement reticle.
 *
 * Authored **lying flat in the XZ plane with +Y up**. This is not cosmetic:
 * `EnvironmentRaycastTarget` orients the host object so its +Y axis matches the
 * detected surface normal, so a mesh built standing upright would lie down on
 * the floor and stand up on walls — exactly backwards.
 *
 * A radial alpha falloff keeps the edge from reading as a hard disc floating
 * above the surface, which is what makes a reticle look pasted on rather than
 * lying on the table.
 */
import {
  CanvasTexture,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
} from 'three';

const DISC_METERS = 0.26;
const TEXTURE_PX = 256;
const GRID_PX = 32;
const POST_HEIGHT = 0.055;
const POST_BASE_Y = 0.16;
const BOB_AMPLITUDE = 0.014;
const BOB_HZ = 2;

export interface Reticle {
  /** Add this to the scene / attach to the raycast-target entity. */
  readonly group: Group;
  /** Advance the post's bob. Pass elapsed seconds. */
  updateBob(elapsedSeconds: number): void;
  /** Release GPU resources. */
  dispose(): void;
}

export function createReticle(): Reticle {
  const group = new Group();
  group.name = 'placement-reticle';
  // Hidden until the raycast system reports a hit; it flips `visible` itself.
  group.visible = false;

  const texture = new CanvasTexture(buildDiscTexture());
  const discMaterial = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    // Without this the disc punches a hole in anything drawn after it, which
    // in passthrough means a grey patch where the real surface should show.
    depthWrite: false,
    side: DoubleSide,
  });
  const discGeometry = new PlaneGeometry(DISC_METERS, DISC_METERS);
  const disc = new Mesh(discGeometry, discMaterial);
  disc.rotation.x = -Math.PI / 2;
  group.add(disc);

  const postGeometry = new CylinderGeometry(0.0125, 0.0125, POST_HEIGHT, 12);
  const postMaterial = new MeshBasicMaterial({ color: 0x46e0c8, transparent: true, opacity: 0.9 });
  const post = new Mesh(postGeometry, postMaterial);
  post.position.y = POST_BASE_Y;
  group.add(post);

  return {
    group,
    updateBob(elapsedSeconds) {
      post.position.y = POST_BASE_Y + Math.sin(elapsedSeconds * Math.PI * BOB_HZ) * BOB_AMPLITUDE;
    },
    dispose() {
      discGeometry.dispose();
      discMaterial.dispose();
      texture.dispose();
      postGeometry.dispose();
      postMaterial.dispose();
      group.clear();
    },
  };
}

/**
 * Grid disc: a square grid, then a radial `destination-in` gradient that
 * feathers everything outside the circle to transparent.
 */
function buildDiscTexture(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_PX;
  canvas.height = TEXTURE_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.clearRect(0, 0, TEXTURE_PX, TEXTURE_PX);
  ctx.strokeStyle = 'rgba(190, 245, 235, 0.85)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let p = 0; p <= TEXTURE_PX; p += GRID_PX) {
    ctx.moveTo(p, 0);
    ctx.lineTo(p, TEXTURE_PX);
    ctx.moveTo(0, p);
    ctx.lineTo(TEXTURE_PX, p);
  }
  ctx.stroke();

  const half = TEXTURE_PX / 2;
  const falloff = ctx.createRadialGradient(half, half, half * 0.25, half, half, half);
  falloff.addColorStop(0, 'rgba(255, 255, 255, 1)');
  falloff.addColorStop(0.75, 'rgba(255, 255, 255, 0.85)');
  falloff.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = falloff;
  ctx.fillRect(0, 0, TEXTURE_PX, TEXTURE_PX);

  return canvas;
}
